import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from 'react';
import { TrendingUp, PiggyBank, Users, MessageCircle, ScanLine, CreditCard, Target, Sparkles } from 'lucide-react';

const FEATURES = [
  {
    key: 'tracking',
    icon: TrendingUp,
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50 dark:bg-blue-500/10',
    textColor: 'text-blue-600 dark:text-blue-400',
  },
  {
    key: 'budgets',
    icon: Target,
    color: 'bg-amber-500',
    lightColor: 'bg-amber-50 dark:bg-amber-500/10',
    textColor: 'text-amber-600 dark:text-amber-400',
  },
  {
    key: 'goals',
    icon: PiggyBank,
    color: 'bg-emerald-500',
    lightColor: 'bg-emerald-50 dark:bg-emerald-500/10',
    textColor: 'text-emerald-600 dark:text-emerald-400',
  },
  {
    key: 'couple',
    icon: Users,
    color: 'bg-pink-500',
    lightColor: 'bg-pink-50 dark:bg-pink-500/10',
    textColor: 'text-pink-600 dark:text-pink-400',
  },
  {
    key: 'chat',
    icon: MessageCircle,
    color: 'bg-violet-500',
    lightColor: 'bg-violet-50 dark:bg-violet-500/10',
    textColor: 'text-violet-600 dark:text-violet-400',
  },
  {
    key: 'family',
    icon: Users,
    color: 'bg-cyan-500',
    lightColor: 'bg-cyan-50 dark:bg-cyan-500/10',
    textColor: 'text-cyan-600 dark:text-cyan-400',
  },
  {
    key: 'credits',
    icon: CreditCard,
    color: 'bg-red-500',
    lightColor: 'bg-red-50 dark:bg-red-500/10',
    textColor: 'text-red-600 dark:text-red-400',
  },
  {
    key: 'scan',
    icon: ScanLine,
    color: 'bg-brand-500',
    lightColor: 'bg-brand-50 dark:bg-brand-500/10',
    textColor: 'text-brand-600 dark:text-brand-400',
  },
];

function MockupFeature({ featureKey }) {
  const [isInteracting, setIsInteracting] = useState(false);

  const mockups = {
    tracking: (
      <div className="space-y-2">
        <div className="flex gap-1 mb-2">
          {['Todos', 'Gastos', 'Ingresos'].map((f, i) => (
            <span key={f} className={`text-[8px] font-semibold px-2 py-0.5 rounded-full ${i === 0 ? 'bg-brand-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'}`}>{f}</span>
          ))}
        </div>
        {[
          { icon: '🍽️', name: 'Supermercado', amount: '-$85,500', color: 'text-red-400' },
          { icon: '💰', name: 'Salario', amount: '+$3,500,000', color: 'text-emerald-500' },
          { icon: '🚗', name: 'Uber', amount: '-$12,000', color: 'text-red-400' },
        ].map((item) => (
          <div key={item.name} className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs">{item.icon}</div>
            <span className="flex-1 text-[10px] text-gray-700 dark:text-gray-300">{item.name}</span>
            <span className={`text-[10px] font-bold ${item.color}`}>{item.amount}</span>
          </div>
        ))}
      </div>
    ),
    budgets: (
      <div className="space-y-2">
        {[
          { name: 'Comida', pct: 65, color: 'bg-amber-400' },
          { name: 'Transporte', pct: 40, color: 'bg-blue-400' },
          { name: 'Entretenimiento', pct: 90, color: 'bg-pink-400' },
        ].map((item) => (
          <div key={item.name}>
            <div className="flex justify-between text-[9px] mb-1">
              <span className="text-gray-500">{item.name}</span>
              <span className="font-bold text-gray-500">{item.pct}%</span>
            </div>
            <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className={`h-full ${item.color} rounded-full`} style={{ width: `${item.pct}%` }} />
            </div>
          </div>
        ))}
      </div>
    ),
    goals: (
      <div className="space-y-2">
        {[
          { icon: '✈️', name: 'Vacaciones', pct: 40 },
          { icon: '💻', name: 'Laptop', pct: 100, complete: true },
        ].map((g) => (
          <div key={g.name}>
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-sm">{g.icon}</span>
              <span className="text-[10px] font-semibold flex-1">{g.name}</span>
              <span className={`text-[9px] font-semibold ${g.complete ? 'text-emerald-500' : 'text-gray-400'}`}>{g.complete ? '✓' : `${g.pct}%`}</span>
            </div>
            <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${g.complete ? 'bg-emerald-400' : 'bg-gradient-to-r from-brand-500 to-accent-500'}`} style={{ width: `${g.pct}%` }} />
            </div>
          </div>
        ))}
      </div>
    ),
    couple: (
      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-bold">FR</div>
          <div className="text-lg">💕</div>
          <div className="w-8 h-8 rounded-full bg-pink-500 flex items-center justify-center text-white text-xs font-bold">AN</div>
        </div>
        <div className="text-xs font-bold text-gray-900 dark:text-white">$4,850,000</div>
        <div className="text-[9px] text-gray-400">Balance compartido</div>
      </div>
    ),
    chat: (
      <div className="space-y-2">
        <div className="flex justify-end">
          <div className="bg-brand-500 text-white text-[9px] px-2 py-1 rounded-lg rounded-br-sm">¿Cuánto gasté?</div>
        </div>
        <div className="flex justify-start">
          <div className="bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-[9px] px-2 py-1 rounded-lg rounded-bl-sm">Gastaste $1,475,490</div>
        </div>
      </div>
    ),
    family: (
      <div className="space-y-1.5">
        {[
          { name: 'Francisco', amount: '$820k', color: 'bg-blue-500' },
          { name: 'Ana', amount: '$345k', color: 'bg-pink-500' },
          { name: 'Sofía', amount: '$180k', color: 'bg-purple-500' },
        ].map((m) => (
          <div key={m.name} className="flex items-center gap-2">
            <div className={`w-5 h-5 rounded-full ${m.color}`} />
            <span className="flex-1 text-[10px]">{m.name}</span>
            <span className="text-[10px] font-bold">{m.amount}</span>
          </div>
        ))}
      </div>
    ),
    credits: (
      <div className="space-y-2">
        {[
          { bank: 'BBVA', desc: 'TV', pct: 40, color: '#004481' },
          { bank: 'Santander', desc: 'Préstamo', pct: 30, color: '#ec0000' },
        ].map((c) => (
          <div key={c.desc} className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-lg flex items-center justify-center text-[8px] font-bold text-white" style={{ backgroundColor: c.color }}>{c.bank[0]}</div>
            <div className="flex-1">
              <div className="text-[10px] font-medium">{c.desc}</div>
              <div className="h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${c.pct}%` }} />
              </div>
            </div>
          </div>
        ))}
      </div>
    ),
    scan: (
      <div className="text-center py-2">
        <div className="w-10 h-10 rounded-xl bg-brand-100 dark:bg-brand-500/20 flex items-center justify-center text-xl mx-auto mb-2">📸</div>
        <div className="text-[9px] font-semibold text-brand-600 dark:text-brand-400">Escanear boleta</div>
        <div className="text-[8px] text-gray-400 mt-1">Extrae datos automáticamente</div>
      </div>
    ),
  };

  return (
    <div 
      className={`mt-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3 select-none transition-all ${isInteracting ? 'ring-2 ring-brand-500/30' : ''}`}
      onMouseEnter={() => setIsInteracting(true)}
      onMouseLeave={() => setIsInteracting(false)}
    >
      {mockups[featureKey] || null}
    </div>
  );
}

export default function SolutionSection2() {
  const { t } = useTranslation();
  const [visibleCards, setVisibleCards] = useState([]);
  const cardsRef = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.dataset.index);
            setVisibleCards((prev) => (prev.includes(index) ? prev : [...prev, index]));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    cardsRef.current.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" className="py-20 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-100 dark:bg-brand-500/10 text-brand-600 dark:text-brand-400 text-xs font-semibold mb-4">
            <Sparkles size={14} />
            TODAS LAS FUNCIONES
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-gray-900 dark:text-white mb-4">
            Todo lo que necesitas para{' '}
            <span className="text-gradient">ordenar tus finanzas</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Desde el registro simple de gastos hasta el control completo de tu vida financiera en pareja.
          </p>
        </div>

        {/* Feature grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURES.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.key}
                ref={(el) => (cardsRef.current[i] = el)}
                data-index={i}
                className={`group relative bg-white dark:bg-gray-900 rounded-2xl p-5 border border-gray-100 dark:border-gray-800 
                           hover:border-brand-200 dark:hover:border-brand-800 hover:shadow-lg transition-all duration-500
                           ${visibleCards.includes(i) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${i * 75}ms` }}
              >
                {/* Try it badge */}
                <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-[10px] font-semibold text-brand-500 bg-brand-50 dark:bg-brand-500/10 px-2 py-0.5 rounded-full">
                    ¡Pruébalo!
                  </span>
                </div>

                {/* Icon */}
                <div className={`w-10 h-10 rounded-xl ${feature.lightColor} flex items-center justify-center mb-3`}>
                  <Icon size={20} className={feature.textColor} />
                </div>

                {/* Content */}
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">
                  {t(`landing.solution.items.${feature.key}.title`)}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed mb-3">
                  {t(`landing.solution.items.${feature.key}.description`)}
                </p>

                {/* Interactive mockup */}
                <MockupFeature featureKey={feature.key} />
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Y mucho más: Exportar datos, múltiples monedas, dark mode, notificaciones...
          </p>
        </div>
      </div>
    </section>
  );
}
